import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconChevronLeft,
  IconCirclePlusFilled,
  IconLifebuoyOff,
} from "@tabler/icons-react";
import MProfilePicker from "@/components/ui/atoms/MProfilePicker";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MLoader from "@/components/ui/atoms/MLoader";
import MBadge from "@/components/ui/atoms/MBadge";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import {
  MedicineStatus,
  MedicineTimingRuleType,
  PillboxItem,
} from "@/types/pillbox";
import { formatMedicineTitle } from "./utils/formatMedicine";
import MedicineCard from "./components/MedicineCard";
import StopMedicationModal from "./components/StopMedicationModal";
import styles from "./SosLanding.module.css";

const SosLanding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;
  const { fetchLedger, stopItem, toggleRefill } = usePillbox(memberId);

  // Forwards the List/Schedule tab the landing screen was on before
  // navigating here (see PillboxLanding's quick-action onClick), so going
  // back restores it instead of always landing on the default Schedule tab.
  const handleBackToLanding = () => {
    const fromView = (
      location.state as { fromView?: "schedule" | "list" } | null
    )?.fromView;
    navigate("/pillbox", { state: { viewMode: fromView } });
  };

  const [items, setItems] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stopTarget, setStopTarget] = useState<PillboxItem | null>(null);
  const [stopping, setStopping] = useState(false);

  const loadItems = useCallback(() => {
    if (!memberId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchLedger(MedicineStatus.ACTIVE)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [memberId, fetchLedger]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const sosItems = useMemo(
    () =>
      items.filter(
        (item) => item.timingRuleType === MedicineTimingRuleType.CONDITIONAL
      ),
    [items]
  );

  const groups = useMemo(() => {
    const map = new Map<string, PillboxItem[]>();
    for (const item of sosItems) {
      const key =
        item.reasonForTaking?.trim() || t("pillbox.sosLanding.otherGroup");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([label, groupItems]) =>
          [
            label,
            [...groupItems].sort((a, b) => a.name.localeCompare(b.name)),
          ] as const
      );
  }, [sosItems, t]);

  const handleAdd = () =>
    navigate("/pillbox/add-medicine", { state: { defaultCategory: "SOS" } });

  const handleSelectItem = (item: PillboxItem) =>
    navigate(`/pillbox/${memberId}/${item.itemType}/${item.id}`);

  const handleEditDetails = (item: PillboxItem) =>
    navigate("/pillbox/add-medicine", {
      state: { mode: "edit", item },
    });

  const handleToggleRefill = async (item: PillboxItem) => {
    const nextNeedsRefill = !item.needsRefill;
    try {
      await toggleRefill(item.itemType, item.id, nextNeedsRefill);
      showToast({
        variant: "success",
        message: t(
          nextNeedsRefill
            ? "pillbox.shoppingList.addedToast"
            : "pillbox.shoppingList.removedToast",
          { name: formatMedicineTitle(item) }
        ),
      });
      loadItems();
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    }
  };

  const handleConfirmStop = async () => {
    if (!stopTarget) return;
    setStopping(true);
    try {
      await stopItem(stopTarget.itemType, stopTarget.id);
      showToast({
        variant: "success",
        message: t("pillbox.stopModal.successMessage", {
          name: formatMedicineTitle(stopTarget),
          member: memberDisplayName,
        }),
      });
      setStopTarget(null);
      loadItems();
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToLanding}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
          <h1 className={styles.title}>{t("pillbox.sosLanding.title")}</h1>
        </div>
        <div className={styles.switchMemberRow}>
          <MProfilePicker />
          <button
            type="button"
            className={styles.headerAddButton}
            onClick={handleAdd}
            aria-label={t("pillbox.add")}
          >
            <IconCirclePlusFilled size={32} />
          </button>
        </div>
        <p className={styles.subtitle}>
          {groups.length === 0
            ? t("pillbox.sosLanding.subtitleEmpty")
            : t("pillbox.sosLanding.subtitle", { member: memberDisplayName })}
        </p>
      </div>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.loaderWrapper}>
            <MLoader />
          </div>
        ) : groups.length === 0 ? (
          <MEmptyCard
            className={styles.emptyCard}
            icon={<IconLifebuoyOff size={64} stroke={1.5} color="#AD95D1" />}
            primaryTitle={t("pillbox.sosLanding.emptyTitle")}
          />
        ) : (
          groups.map(([label, groupItems]) => (
            <div className={styles.group} key={label}>
              <MBadge className={styles.groupBadge}>{label}</MBadge>
              <div className={styles.groupCard}>
                {groupItems.map((item) => (
                  <MedicineCard
                    key={item.id}
                    itemType={item.itemType}
                    title={formatMedicineTitle(item)}
                    trailingIcon="dots"
                    onClick={() => handleSelectItem(item)}
                    onEditDetails={() => handleEditDetails(item)}
                    needsRefill={item.needsRefill}
                    showRefillBadge={false}
                    onToggleRefill={() => handleToggleRefill(item)}
                    onStopMedication={() => setStopTarget(item)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <StopMedicationModal
        open={!!stopTarget}
        onClose={() => setStopTarget(null)}
        onConfirm={handleConfirmStop}
        loading={stopping}
      />
    </div>
  );
};

export default SosLanding;
